import React from "react";

type Props = {
  children: React.ReactNode;
};

type State = {
  error?: Error;
};

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = {};

  private visibilityHandler: (() => void) | null = null;

  static getDerivedStateFromError(error: Error): State {
    // Auto-recover from DOM manipulation errors (browser extensions/translation)
    if (/NotFoundError|removeChild|insertBefore|appendChild/.test(error.message)) {
      console.warn("[ErrorBoundary] Suppressing recoverable DOM error:", error.message);
      return {};
    }
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (/NotFoundError|removeChild|insertBefore|appendChild/.test(error.message)) {
      console.warn("[ErrorBoundary] Recovered from DOM manipulation error");
      return;
    }
    console.error("App render error:", error);
    console.error("Component stack:", info.componentStack);
  }

  componentDidMount() {
    this.visibilityHandler = () => {
      if (document.visibilityState === "visible" && this.state.error) {
        // Auto-recover when user returns from lock screen / background
        console.log("[ErrorBoundary] Auto-recovering on visibility change");
        this.setState({ error: undefined });
      }
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  componentWillUnmount() {
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
    }
  }

  handleRetry = () => {
    this.setState({ error: undefined });
  };

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
          <main className="container max-w-md py-16 text-center">
            <h1 className="text-2xl font-bold">Something went wrong</h1>
            <p className="mt-2 text-muted-foreground">
              A temporary error occurred. Tap below to reload.
            </p>
            <pre className="mt-4 whitespace-pre-wrap rounded-lg border bg-card p-4 text-sm text-foreground text-left">
              {this.state.error.message}
            </pre>
            <button
              onClick={this.handleRetry}
              className="mt-6 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-base active:scale-95 transition-transform"
            >
              Try Again
            </button>
          </main>
        </div>
      );
    }

    return this.props.children;
  }
}
